import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FacilityID } from "../../../constants/defaultKeys";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import {
  getAppointmentListApi,
  getPractitionerSearchApi,
} from "../../../services/receptionist.services";
import useQuery from "../../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../../helpers/urlFilters";
import btnStyles from "../../../styles/Custom/Button.module.css";
import AppointmentsTable from "./AppointmentsTable";
import AppointmentsCalendar, { calendarRange } from "./AppointmentsCalendar";
import AppointsModal from "./AppointsModal";
import { CalendarOutlined, PlusOutlined } from "@ant-design/icons";
// Same table glyph as the Progress Scales table/graph toggle
import tableIcon from "../../../assets/images/table.svg";
import { Button, DatePicker, Segmented, Space, Tooltip } from "antd";
import dayjs from "dayjs";
import useIsMobile from "../../../hooks/useIsMobile";
import useReceptionistMobilePickerProps from "../receptionistMobilePickerProps";
import { UiDataTable } from "../../ui";
import styles from "./Appointments.module.css";

const { RangePicker } = DatePicker;
const dateFormat = "YYYY-MM-DD";
const MAX_CALENDAR_PAGES = 10;

const Appointments = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const page = parsePageParam(params);
  const searchQuery = parseSearchParam(params);
  const isMobile = useIsMobile();
  const mobilePickerProps = useReceptionistMobilePickerProps({ range: true });
  const { userDetails } = useContext(UserContext);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calendarMode, setCalendarMode] = useState("week");
  const [roleByDoctorId, setRoleByDoctorId] = useState({});
  const roleLookupRef = useRef({});

  const view = params?.view === "calendar" ? "calendar" : "table";
  const facilityId =
    Number(getCookie(FacilityID)) || userDetails?.currFacility;

  const startDateStr = params?.start_date || dayjs().format(dateFormat);
  const endDateStr = params?.end_date || dayjs().format(dateFormat);
  const startDate = dayjs(startDateStr);
  const endDate = dayjs(endDateStr);

  const calendarSpan = calendarRange(startDate, calendarMode);
  const rangeStart = view === "calendar" ? calendarSpan.start : startDate;
  const rangeEnd = view === "calendar" ? calendarSpan.end : endDate;
  const rangeStartStr = rangeStart.format(dateFormat);
  const rangeEndStr = rangeEnd.format(dateFormat);

  const setPage = (newPage) => {
    updateUrlSearchParams(navigate, { page: newPage });
  };

  const refreshPage = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    const baseParams = {
      facility: facilityId,
      start_date: rangeStartStr,
      end_date: rangeEndStr,
    };
    if (searchQuery) {
      baseParams.search = searchQuery;
    }

    let cancelled = false;
    setLoading(true);

    const load = async () => {

      if (view !== "calendar") {
        const queryString = new URLSearchParams({
          ...baseParams,
          page: searchQuery ? 1 : page,
        });
        const res = await getAppointmentListApi(queryString);
        return res?.data;
      }

      let current = 1;
      let totalPages = 1;
      let rows = [];
      while (current <= totalPages && current <= MAX_CALENDAR_PAGES) {
        const queryString = new URLSearchParams({
          ...baseParams,
          page: current,
        });
        const res = await getAppointmentListApi(queryString);
        rows = rows.concat(res?.data?.data || []);
        totalPages = res?.data?.total_pages || 1;
        current += 1;
      }
      return { data: rows, total_count: rows.length };
    };

    load()
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
      })
      .catch((err) => {
        if (!cancelled) console.log(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    rangeStartStr,
    rangeEndStr,
    refresh,
    searchQuery,
    page,
    view,
    facilityId,
  ]);

  /*
   * The appointments payload carries `doctor` (a practitioner id) and
   * `doctor_name`, but not the practitioner's role, and there is no
   * list-practitioners endpoint that works without a search term. Look each
   * unseen doctor up by name and match on id; results are cached for the
   * session so a week of appointments costs at most one call per doctor.
   */
  useEffect(() => {
    if (view !== "calendar" || !facilityId) return;
    const rows = data?.data || [];
    const pending = [
      ...new Set(
        rows
          .filter((r) => r?.doctor && r?.doctor_name)
          .filter((r) => roleLookupRef.current[r.doctor] === undefined)
          .map((r) => `${r.doctor}|${r.doctor_name}`)
      ),
    ];
    if (!pending.length) return;

    let cancelled = false;

    Promise.all(
      pending.map((entry) => {
        const [doctorId, doctorName] = entry.split("|");
        roleLookupRef.current[doctorId] = null; // mark in-flight
        const query = doctorName.trim().split(/\s+/)[0];
        return getPractitionerSearchApi(
          `query=${encodeURIComponent(query)}&facility=${facilityId}&role=practitioners`
        )
          .then((res) => {
            const match = (res?.data || []).find(
              (p) => String(p?.id) === String(doctorId)
            );
            return [doctorId, match?.role || null];
          })
          .catch(() => [doctorId, null]);
      })
    ).then((pairs) => {
      if (cancelled) return;
      const next = {};
      pairs.forEach(([id, role]) => {
        roleLookupRef.current[id] = role;
        if (role) next[id] = role;
      });
      if (Object.keys(next).length) {
        setRoleByDoctorId((prev) => ({ ...prev, ...next }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [data, view, facilityId]);

  const onContactSearch = (e) => {
    updateUrlSearchParams(navigate, {
      search: e?.target?.value || null,
      page: 1,
    });
  };

  const onAnchorChange = (date) => {
    updateUrlSearchParams(navigate, {
      start_date: date.format(dateFormat),
      page: 1,
    });
  };

  const toolbarTrailing = (
    <Space
      className={styles.receptionAppointmentsActions}
      size={isMobile ? "small" : "middle"}
    >
      {view === "table" && (
        <RangePicker
          allowClear={false}
          className={styles.receptionAppointmentsDateRange}
          style={{ background: "#fff" }}
          value={[startDate, endDate]}
          onChange={(dates) => {
            if (dates?.[0] && dates?.[1]) {
              updateUrlSearchParams(navigate, {
                start_date: dates[0].format(dateFormat),
                end_date: dates[1].format(dateFormat),
                page: 1,
              });
            }
          }}
          {...mobilePickerProps}
        />
      )}
      <Segmented
        className={styles.receptionAppointmentsViewToggle}
        value={view === "calendar" ? "calendar" : "table"}
        options={[
          {
            value: "table",
            icon: (
              <Tooltip title="Table view">
                <img src={tableIcon} alt="" aria-hidden="true" />
              </Tooltip>
            ),
          },
          {
            value: "calendar",
            icon: (
              <Tooltip title="Calendar view">
                <CalendarOutlined />
              </Tooltip>
            ),
          },
        ]}
        onChange={(value) =>
          updateUrlSearchParams(navigate, {
            view: value === "calendar" ? "calendar" : null,
            page: 1,
          })
        }
      />
      <Button
        type="primary"
        className={`${btnStyles.mdBtn} ${styles.receptionAppointmentsAddBtn}`}
        onClick={() => setOpen(true)}
      >
        <PlusOutlined /> Appointments
      </Button>
    </Space>
  );

  return (
    <div className={styles.receptionAppointments}>
      <UiDataTable
        showHeader={false}
        shellClassName="ui-table-fill-shell"
        onSearch={onContactSearch}
        searchValue={searchQuery || ""}
        searchPlaceholder="Search for appointments"
        showSearch={view === "table"}
        toolbarTrailing={toolbarTrailing}
      >
        {view === "calendar" ? (
          <AppointmentsCalendar
            data={data?.data}
            loading={loading}
            anchor={startDate}
            onAnchorChange={onAnchorChange}
            mode={calendarMode}
            onModeChange={setCalendarMode}
            roleByDoctorId={roleByDoctorId}
            onRefresh={refreshPage}
          />
        ) : (
          <AppointmentsTable
            loading={loading}
            data={data}
            refresh={refreshPage}
            page={page}
            setPage={setPage}
          />
        )}
      </UiDataTable>

      {open && (
        <AppointsModal open={open} setOpen={setOpen} refresh={refreshPage} />
      )}
    </div>
  );
};

export default Appointments;
